import { categoryApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  SimpleProductCategoryI,
} from "../../interfaces";
import {
  ProductI,
  ProductCategoryI,
  GetProductParamsI,
  GetCategoryParamsI,
} from "@interfaces";
import { AxiosError } from "axios";

// ── helpers ──────────────────────────────
const token = () => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  return t;
};

const handleErr = (err: unknown) => {
  const obj = (err as AxiosError).response;
  if (obj?.status === 401) LogoutSystem();
  throw new Error((obj?.data as Error).message);
};

export const getAllCategories = async (): Promise<
  ServiceResponseI<SimpleProductCategoryI[]>
> => {
  try {
    const { data } = await categoryApi.get<
      ServiceResponseI<SimpleProductCategoryI[]>
    >("/all-no-pagination", {
      headers: { Authorization: token() },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// ── categorías ────────────────────────────
const getCategories = async ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetCategoryParamsI): Promise<ServiceResponseI<DataI<ProductCategoryI>>> => {
  try {
    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (clientId) params.append("clientId", clientId.toString());
    if (status !== undefined) params.append("status", String(status));

    const { data } = await categoryApi.get<
      ServiceResponseI<DataI<ProductCategoryI>>
    >("", {
      params,
      headers: { Authorization: token() },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const categoryPost = async <T>(
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await categoryApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const categoryPut = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await categoryApi.put<ServiceResponseI<string>>(
      `/${id}`,
      payload,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const categoryDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await categoryApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const categorySetStatus = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await categoryApi.patch<ServiceResponseI<string>>(
      `/set-status/${id}`,
      {},
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const useCategories = (params: GetCategoryParamsI) => {
  const categoriesQuery = useQuery(
    ["categories", params],
    () => getCategories(params),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { categoriesQuery };
};
